import { useState, useCallback, useMemo, useRef } from 'react';
import { useField } from 'formik';

import { isBooleanSearch, isInvalidBooleanSearch } from '../../../../../../helpers/booleanValidator';
import { useJobTitleFilter } from './useJobTitleFilter';

export const useFormikJobTitleMultiSelect = ({ name, sharedList, orderBy }) => {
  const [field, , helpers] = useField(name);
  const [isValidSearch, setIsValidSearch] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const selectRef = useRef(null);

  const { onSearchChange } = useJobTitleFilter({
    sharedList,
    orderBy,
    setFilters: () => {},
  });

  const selected = useMemo(
    () =>
      (field.value || []).map((item) => ({
        ...item,
        isEditable: isBooleanSearch(item.value),
      })),
    [field.value],
  );

  const handleEditValue = useCallback(
    (data) => {
      // Remove the item and put its value in the input for editing
      const newValues = (field.value || []).filter((item) => item.value !== data.value);
      helpers.setValue(newValues);
      setInputValue(data.label);
      setIsValidSearch(true);

      // Focus the select
      if (selectRef.current) {
        selectRef.current.focus();
      }
    },
    [field.value, helpers],
  );

  const handleChange = useCallback(
    (e) => {
      const { value } = e.target;

      // Validate any boolean query in the new value (handles both additions and edits)
      const previousBoolean = selected.find((item) => item.isEditable);
      const nextBoolean = value.find((item) => isBooleanSearch(item.value));

      if (nextBoolean && (!previousBoolean || nextBoolean.value !== previousBoolean.value)) {
        if (isInvalidBooleanSearch(nextBoolean.value)) {
          setIsValidSearch(false);
          return;
        }
      }

      const booleanSearch = selected.find((item) => item.isEditable);
      const addedBooleanSearch = value.length > selected.length && isBooleanSearch(value[value.length - 1].value);

      // Only allow one boolean search at a time
      const newValue =
        addedBooleanSearch && booleanSearch ? value.filter((item) => item.value !== booleanSearch.value) : value;

      setIsValidSearch(true);
      setInputValue('');
      helpers.setValue(newValue);
    },
    [selected, helpers],
  );

  const handleInputChange = useCallback((newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);

      // Validate boolean search immediately for real-time feedback
      if (isBooleanSearch(newValue)) {
        const isInvalid = isInvalidBooleanSearch(newValue);
        setIsValidSearch(!isInvalid);
      } else {
        setIsValidSearch(true);
      }
    }
    if (actionMeta.action === 'set-value' || actionMeta.action === 'menu-close') {
      setInputValue('');
      setIsValidSearch(true);
    }
  }, []);

  const isValidNewOption = useCallback((inputVal) => {
    if (!inputVal?.trim()) return false;

    if (isBooleanSearch(inputVal)) {
      const isInvalid = isInvalidBooleanSearch(inputVal);
      setIsValidSearch(!isInvalid);
      return !isInvalid;
    }

    setIsValidSearch(true);
    return true;
  }, []);

  const loadOptions = useCallback(
    async (inputVal) => {
      if (isBooleanSearch(inputVal)) {
        // Don't search API for boolean queries
        const isInvalid = isInvalidBooleanSearch(inputVal);
        setIsValidSearch(!isInvalid);
        return [];
      }
      setIsValidSearch(true);
      return onSearchChange(inputVal);
    },
    [onSearchChange],
  );

  return {
    field,
    selected,
    selectRef,
    isValidSearch,
    inputValue,
    handleEditValue,
    handleChange,
    handleInputChange,
    isValidNewOption,
    loadOptions,
  };
};
